import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { differenceInMonths } from 'date-fns';
import { useBaby } from './BabyContext';
import { StorageAdapter } from '@/lib/storage-adapter';

export interface Celebration {
  emoji: string;
  title: string;
  subtitle: string;
}

interface CelebrationContextValue {
  current: Celebration | null;
  dismiss: () => void;
  celebrate: (c: Celebration) => void;
}

const CelebrationContext = createContext<CelebrationContextValue>({
  current: null,
  dismiss: () => {},
  celebrate: () => {},
});

export const useCelebration = () => useContext(CelebrationContext);

export const CelebrationProvider = ({ children }: { children: React.ReactNode }) => {
  const [queue, setQueue] = useState<Celebration[]>([]);
  const { activeBaby } = useBaby();
  const checkedBabyRef = useRef<string | null>(null);

  const celebrate = useCallback((c: Celebration) => {
    setQueue(q => [...q, c]);
  }, []);

  const dismiss = useCallback(() => {
    setQueue(q => q.slice(1));
  }, []);

  // Anniversary check — runs once per baby per session, at most once per day
  useEffect(() => {
    if (!activeBaby) return;
    if (checkedBabyRef.current === activeBaby.id) return;
    checkedBabyRef.current = activeBaby.id;

    const dob = new Date(activeBaby.dob);
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const lastKey = `last_anniversary_${activeBaby.id}`;
    if (StorageAdapter.getItem(lastKey) === todayStr) return;

    const totalMonths = differenceInMonths(today, dob);
    if (totalMonths < 1) return;

    // Only fire on the same calendar day-of-month as birth
    if (dob.getDate() !== today.getDate()) return;

    let celebration: Celebration | null = null;

    if (totalMonths >= 1 && totalMonths <= 6) {
      celebration = {
        emoji: '🎂',
        title: `${activeBaby.name.split(' ')[0]} is ${totalMonths} month${totalMonths === 1 ? '' : 's'} old!`,
        subtitle: `${totalMonths} month${totalMonths === 1 ? '' : 's'} of love, growth, and beautiful moments. You're doing an incredible job!`,
      };
    } else if (totalMonths % 12 === 0) {
      const years = totalMonths / 12;
      const ordinal = years === 1 ? '1st' : years === 2 ? '2nd' : years === 3 ? '3rd' : `${years}th`;
      celebration = {
        emoji: '🎉',
        title: `Happy ${ordinal} Birthday, ${activeBaby.name.split(' ')[0]}!`,
        subtitle: `A whole year of love, laughter, and milestones. Here's to many more!`,
      };
    }

    if (celebration) {
      StorageAdapter.setItem(lastKey, todayStr);
      setQueue(q => [celebration!, ...q]);
    }
  }, [activeBaby]);

  return (
    <CelebrationContext.Provider value={{ current: queue[0] ?? null, dismiss, celebrate }}>
      {children}
    </CelebrationContext.Provider>
  );
};
